import React, { useState } from 'react';
import { Phone, MessageCircle, NotebookPen } from 'lucide-react';
import { addCustomerHistory } from '../hooks/useCustomerHistory';
import { useActionLog } from '../hooks/useActionLog';

const NextActionModal = ({ isOpen, onClose, customer }) => {
    const { sendActionLog } = useActionLog();
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyNote, setHistoryNote] = useState('');
    const [savingHistory, setSavingHistory] = useState(false);

    const name = customer?.name || '고객님';
    const phone = customer?.phone || '';
    const template = `안녕하세요 ${name}님, 방금 상담 내용 정리해 공유드립니다. 편하실 때 확인 부탁드립니다.`;

    if (!isOpen) return null;

    const callNow = () => {
        if (phone) window.location.href = `tel:${phone}`;
    };

    const isMobileDevice = () => {
        try {
            const ua = navigator.userAgent || navigator.vendor || window.opera;
            return /Android|iPhone|iPad|iPod/i.test(ua);
        } catch (_) {
            return false;
        }
    };

    const canUseWebShare = () => {
        try {
            return !!navigator.share;
        } catch (_) {
            return false;
        }
    };

    const shareToKakao = async () => {
        if (!isMobileDevice()) {
            try { alert('모바일에서만 동작하는 기능입니다'); } catch (_) { }
            return;
        }
        const message = template;

        // 우선 Web Share API 사용: 카카오톡 포함 OS 공유 시트
        if (canUseWebShare()) {
            try {
                await navigator.share({
                    text: message
                });
                onClose && onClose();
                return;
            } catch (e) {
                // 사용자가 취소하거나 미지원
            }
        }

        // 폴백: 메시지 복사 후 안내
        try {
            await navigator.clipboard.writeText(message);
            alert('메시지를 복사했어요. 카카오톡에서 붙여넣기 해주세요.');
        } catch (_) {
            const ta = document.createElement('textarea');
            ta.value = message;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            alert('메시지를 복사했어요. 카카오톡에서 붙여넣기 해주세요.');
        }

        onClose && onClose();
    };

    const openHistoryModal = () => {
        setHistoryNote('');
        setShowHistoryModal(true);
    };

    const saveHistory = async () => {
        if (!customer?.id) return;
        const note = historyNote.trim();
        if (!note) {
            alert('히스토리 내용을 입력해주세요.');
            return;
        }
        try {
            setSavingHistory(true);
            const created = await addCustomerHistory(customer.id, { type: 'note', note });
            if (created) {
                sendActionLog('히스토리_추가', {
                    customerName: customer.name,
                    historyType: 'note'
                });
            }
            try { alert('히스토리를 추가했습니다.'); } catch (_) { }
            alert('이제 <히스토리 보기>버튼을 눌러 잘 기록되었는지 확인해보세요.');
            setShowHistoryModal(false);
            onClose && onClose();
        } catch (e) {
            console.error('히스토리 저장 실패:', e);
            alert(e?.message || '히스토리 저장에 실패했습니다.');
        } finally {
            setSavingHistory(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md">
                <div className="p-5 border-b">
                    <div className="font-bold text-lg">다음 행동</div>
                    <div className="text-sm text-gray-600 mt-1">방금 저장한 고객에게 바로 카톡을 보내거나 히스토리를 기록해보세요.</div>
                </div>
                <div className="p-5 space-y-3">
                    <button onClick={shareToKakao} className="w-full py-3 rounded-xl border bg-yellow-50 hover:bg-yellow-100 flex items-center justify-center gap-2">
                        <MessageCircle className="w-5 h-5 text-yellow-700" /> 카톡으로 보내기
                    </button>
                    <button onClick={callNow} className="w-full py-3 rounded-xl border bg-gray-50 hover:bg-gray-100 flex items-center justify-center gap-2">
                        <Phone className="w-5 h-5" /> 바로 통화
                    </button>
                    <button onClick={openHistoryModal} className="w-full py-3 rounded-xl border bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-2">
                        <NotebookPen className="w-5 h-5 text-blue-700" /> 히스토리 기록하기
                    </button>
                </div>
                <div className="p-4 border-t text-right">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg bg-blue-600 text-white">닫기</button>
                </div>
            </div>

            {showHistoryModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHistoryModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
                        <div className="font-bold text-lg">히스토리 기록하기</div>
                        <div className="text-sm text-gray-600 mt-1">{name}님의 메모를 남겨주세요.</div>
                        <textarea
                            value={historyNote}
                            onChange={(e) => setHistoryNote(e.target.value)}
                            className="w-full mt-3 h-32 border border-gray-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                            placeholder="예) 첫 연락 완료. 내일 오전 10시에 재통화 예정"
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <button className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300" onClick={() => setShowHistoryModal(false)}>취소</button>
                            <button disabled={savingHistory} className={`px-4 py-2 rounded-lg text-white ${savingHistory ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} onClick={saveHistory}>
                                {savingHistory ? '저장 중…' : '저장'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NextActionModal;


